import { ApolloError, gql, useLazyQuery } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { GetMediaUrlInput, Query } from 'generated/graphql';
import { useCallback, useRef } from 'react';
import { useGetUser } from 'v2/core/services/user';

const GET_MEDIA_URL = gql`
  query getMediaUrl(
    $tenantID: ID!
    $userID: ID!
    $fileName: String!
    $fileType: FileType!
  ) {
    getMediaUrl(
      request: {
        tenantID: $tenantID
        userID: $userID
        fileName: $fileName
        fileType: $fileType
      }
    ) {
      url
      s3Key
    }
  }
`;

interface useFetchMediaUrlParams {
  onError?: (error: ApolloError | undefined) => void;
}

export const useFetchMediaUrl = ({ onError }: useFetchMediaUrlParams = {}) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();
  const { showErrorNotification } = useNotificationContext();

  const dependencies = { userID, tenantID };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  const [getMediaUrlQuery, { data, loading, error }] = useLazyQuery<
    Query,
    GetMediaUrlInput
  >(GET_MEDIA_URL, {
    onError: (error) => {
      onError?.(error);

      showErrorNotification({
        contentComponent: 'Failed to fetch the media URL.',
        props: {},
      });
    },
  });

  const getMediaUrl = useCallback(
    async (inputs: Pick<GetMediaUrlInput, 'fileName' | 'fileType'>) => {
      const resp = await getMediaUrlQuery({
        variables: {
          tenantID: dependenciesRef.current.tenantID,
          userID: dependenciesRef.current.userID,
          ...inputs,
        },
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
      });

      return {
        url: resp.data?.getMediaUrl?.url,
        s3Key: resp.data?.getMediaUrl?.s3Key,
      };
    },
    [getMediaUrlQuery],
  );

  return [
    getMediaUrl,
    {
      loading,
      error,
      url: data?.getMediaUrl?.url,
      s3Key: data?.getMediaUrl?.s3Key,
    },
  ] as const;
};
